<template>
  <div class="profile-container">
    <!-- Profile Header -->
    <div class="profile-header">
      <div class="avatar-wrapper">
        <img v-if="userPictureUrl" :src="userPictureUrl" class="avatar-img" />
        <v-icon v-else size="48" color="white">mdi-account</v-icon>
      </div>
      <h1 class="profile-name">{{ user.firstName }} {{ user.lastName }}</h1>
      <p class="profile-email">{{ user.email }}</p>
    </div>

    <!-- Profile Info Card -->
    <div class="profile-content">
      <div class="info-card">
        <div class="card-title">
          <v-icon size="18" color="#666">mdi-account-outline</v-icon>
          <span>{{ $t('profile.txtPersonalInfo') || 'Información personal' }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">{{ $t('profile.txtFirstName') }}</span>
          <span class="info-value">{{ user.firstName }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">{{ $t('profile.txtLastName') }}</span>
          <span class="info-value">{{ user.lastName }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">{{ $t('profile.txtEmail') }}</span>
          <span class="info-value">{{ user.email }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">{{ $t('profile.txtLicenseNumber') }}</span>
          <span class="info-value">{{ user.licenseNumber || '—' }}</span>
        </div>
      </div>

      <!-- Settings Card -->
      <div class="info-card">
        <div class="card-title">
          <v-icon size="18" color="#666">mdi-cog-outline</v-icon>
          <span>{{ $t('profile.txtSettings') || 'Configuración' }}</span>
        </div>

        <div class="setting-row">
          <div class="setting-info">
            <span class="setting-label">{{ $t('profile.txtLanguaje') }}</span>
            <span class="setting-hint">{{ $t('profile.txtLanguageHint') || 'Idioma de la aplicación' }}</span>
          </div>
          <div class="lang-selector">
            <button
              v-for="lang in langs"
              :key="lang"
              class="lang-btn"
              :class="{ 'lang-active': langSelect === lang }"
              @click="langSelect = lang"
            >
              {{ lang.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="actions-section">
        <button class="action-btn btn-secondary" @click="dialogUpdatePass = !dialogUpdatePass">
          <v-icon size="18" color="#666" class="mr-2">mdi-lock-outline</v-icon>
          {{ $t("recover.btnUpdatePassword") }}
        </button>

        <button class="action-btn btn-primary" @click.prevent="saveChange">
          {{ $t("profile.btnSave") }}
        </button>
      </div>
    </div>

    <DialogUpdatePassword v-model="dialogUpdatePass" />
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";
import DialogUpdatePassword from "./UpdatePassword.vue";
export default {
  components: {
    DialogUpdatePassword,
  },
  name: "Profile",
  data() {
    return {
      langs: ["es", "en"],
      langSelect: "es",
      dialogUpdatePass: false,
    };
  },
  methods: {
    ...mapMutations({
      setAppLang: "setLang",
    }),
    ...mapMutations("auth", {
      setUserLang: "setLang",
    }),
    ...mapActions("ui", {
      disabledNavigation: "disabledNavigation",
      activeBackBtn: "activeBackBtn",
      showProgress: "showProgress",
      hideProgress: "hideProgress",
    }),
    saveChange() {
      this.showProgress();
      this.setAppLang(this.langSelect);
      this.setUserLang(this.langSelect);
      this.$root.$i18n.locale = this.langSelect;
      this.$axios
        .put("/profile", {
          firstName: this.user.firstName,
          lastName: this.user.lastName,
          phone: this.user.phone,
          email: this.user.email,
          lang: this.langSelect,
        })
        .then(() => {
          this.hideProgress();
        })
        .catch((error) => {
          this.hideProgress();
          console.log(error);
        });
    },
  },
  computed: {
    ...mapState("auth", {
      user: (state) => state.user,
      userPictureUrl: (state) => state.user?.profilePictureUrl,
    }),
  },
  mounted() {
    this.langSelect = this.user.lang;
    this.disabledNavigation();
    this.activeBackBtn();
  },
};
</script>

<style scoped>
.profile-container {
  min-height: 100vh;
  background: #fafafa;
}

.profile-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 32px 20px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.avatar-wrapper {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.3);
  margin-bottom: 14px;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-name {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 4px;
}

.profile-email {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

.profile-content {
  padding: 16px;
  margin-top: -12px;
}

.info-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}

.info-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-label {
  font-size: 13px;
  color: #888;
}

.info-value {
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
}

.setting-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.setting-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.setting-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.setting-hint {
  font-size: 12px;
  color: #999;
}

.lang-selector {
  display: flex;
  gap: 6px;
}

.lang-btn {
  padding: 6px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.15s ease;
}

.lang-btn:active {
  transform: scale(0.95);
}

.lang-active {
  background: #6200ea;
  border-color: #6200ea;
  color: #fff;
}

.actions-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
}

.action-btn {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-btn:active {
  transform: scale(0.98);
}

.btn-primary {
  background: #6200ea;
  color: #fff;
}

.btn-primary:active {
  background: #5000d0;
}

.btn-secondary {
  background: #fff;
  color: #333;
  border: 1px solid #e0e0e0;
}

.btn-secondary:active {
  background: #f5f5f5;
}
</style>
